<template>
  <div class="sonb">
    <header class="sonb__head" data-tour="seller-onboarding-intro">
      <p class="sonb__eyebrow">판매업체 입점</p>
      <h1>정보 등록 · 약관 · 계약 서명</h1>
      <p>{{ intro }}</p>
      <ol class="sonb__steps" aria-label="진행 단계">
        <li v-for="(step, index) in steps" :key="step.key" class="sonb__step">
          <span class="sonb__step-num">{{ index + 1 }}</span>
          <span class="sonb__step-label">{{ step.label }}</span>
        </li>
      </ol>
      <p class="sonb__head-note">
        제출 후에도 바로 판매되지는 않습니다. 관리자 승인 후 상품 등록이 열립니다.
      </p>
    </header>

    <p v-if="errorMessage" class="sonb__error">
      <AppIcon icon="mdi-alert-outline" :size="16" />
      {{ errorMessage }}
    </p>

    <div class="sonb__layout">
    <form class="sonb__form" @submit.prevent="onSubmit">
      <section
        v-if="pwTemp"
        id="sonb-password"
        class="sui-card sonb__section"
        data-tour="seller-onboarding-password"
      >
        <div class="sui-card__head sonb__section-head">
          <span class="sonb__section-num" aria-hidden="true">{{ stepIndex('password') }}</span>
          <h2 class="sui-card__title">{{ stepLabel('password') }}</h2>
        </div>
        <div class="sui-card__body sonb__body">
          <p class="sonb__section-lead">관리자가 발급한 임시 비밀번호를 본인만 아는 비밀번호로 바꿔 주세요.</p>
          <div class="sui-row sonb__row">
            <div class="sui-field">
              <label for="ob-pw">새 비밀번호</label>
              <input
                id="ob-pw"
                v-model="form.newPassword"
                type="password"
                class="sui-input"
                autocomplete="new-password"
                minlength="8"
                required
              />
              <p class="sui-hint">영문·숫자 포함 8자 이상을 권장합니다.</p>
            </div>
            <div class="sui-field">
              <label for="ob-pw2">새 비밀번호 확인</label>
              <input
                id="ob-pw2"
                v-model="form.newPasswordConfirm"
                type="password"
                class="sui-input"
                autocomplete="new-password"
                minlength="8"
                required
              />
            </div>
          </div>
        </div>
      </section>

      <section id="sonb-profile" class="sui-card sonb__section" data-tour="seller-onboarding-profile">
        <div class="sui-card__head sonb__section-head">
          <span class="sonb__section-num" aria-hidden="true">{{ stepIndex('profile') }}</span>
          <h2 class="sui-card__title">{{ stepLabel('profile') }}</h2>
        </div>
        <div class="sui-card__body sonb__body">
          <p class="sonb__section-lead">
            함우리몰과 주문서에 노출되는 업체 정보입니다. 사업자등록증과 같게 맞추고, 담당자 이메일·휴대폰
            인증을 모두 완료해 주세요.
          </p>
          <div class="sui-row sonb__row">
            <div class="sui-field">
              <label for="ob-store">업체명 (노출명)</label>
              <input id="ob-store" v-model="form.storeName" class="sui-input" required />
            </div>
            <div class="sui-field">
              <label for="ob-owner">대표자명</label>
              <input id="ob-owner" v-model="form.ownerName" class="sui-input" required />
            </div>
          </div>
          <div class="sui-row sonb__row">
            <div class="sui-field">
              <label for="ob-biz">상호</label>
              <input id="ob-biz" v-model="form.bizName" class="sui-input" required />
            </div>
            <div class="sui-field">
              <label for="ob-bizno">사업자등록번호</label>
              <input
                id="ob-bizno"
                :value="form.bizNumber"
                class="sui-input"
                :class="{
                  'is-invalid': Boolean(bizNumberError),
                  'is-valid': isBizNumberInputValid(form.bizNumber),
                }"
                inputmode="numeric"
                maxlength="12"
                placeholder="000-00-00000"
                required
                @input="onBizNumberInput"
              />
              <p v-if="bizNumberError" class="sui-hint sonb__verify-err">{{ bizNumberError }}</p>
              <p v-else-if="isBizNumberInputValid(form.bizNumber)" class="sui-hint sonb__verify-ok">
                형식이 올바릅니다.
              </p>
              <p v-else class="sui-hint">숫자만 넣으면 됩니다. 하이픈(-)은 알아서 들어갑니다.</p>
            </div>
          </div>
          <div class="sui-field">
            <label for="ob-manager">담당자명</label>
            <input id="ob-manager" v-model="form.managerName" class="sui-input" required />
          </div>

          <div class="sui-field">
            <label for="ob-email">담당자 이메일</label>
            <div class="sonb__inline">
              <input
                id="ob-email"
                v-model="form.email"
                type="email"
                class="sui-input"
                autocomplete="email"
                placeholder="seller@example.com"
                required
                :disabled="emailLocked"
              />
              <button
                type="button"
                class="sui-btn"
                :disabled="!isEmailValid(form.email) || emailVerification.verified || emailVerification.sending || emailVerification.resendIn > 0"
                @click="sendEmailCode"
              >
                {{ emailVerification.sendActionLabel('인증발송', '재발송') }}
              </button>
            </div>
            <p v-if="emailVerification.verified" class="sui-hint sonb__verify-ok">이메일 인증이 완료되었습니다.</p>
            <p v-else-if="emailVerification.codeSent" class="sui-hint sonb__verify-ok">
              {{ form.email }}으로 인증번호를 보냈습니다. 받은편지함과 스팸함을 확인해 주세요.
            </p>
            <p v-else class="sui-hint">승인 결과와 새 주문·취소/반품처럼 바로 손봐야 하는 안내를 이 주소로 보냅니다. 인증이 필요합니다.</p>
          </div>

          <div v-if="!emailVerification.verified && emailVerification.codeSent" class="sui-field">
            <label for="ob-email-code">이메일 인증번호</label>
            <div class="sonb__inline">
              <input
                id="ob-email-code"
                v-model="form.emailCode"
                type="text"
                class="sui-input"
                inputmode="numeric"
                maxlength="6"
                placeholder="인증번호 6자리"
                @input="form.emailCode = sanitizeDigitsInput(($event.target as HTMLInputElement).value, 6)"
              />
              <button
                type="button"
                class="sui-btn"
                :disabled="!isVerifyCodeValid(form.emailCode) || emailVerification.verifying"
                @click="confirmEmailCode"
              >
                {{ emailVerification.verifying ? '확인 중' : '확인' }}
              </button>
            </div>
            <p class="sui-hint" :class="{ 'sonb__verify-err': emailVerification.errorMessage }">
              {{ emailVerification.errorMessage || '메일함에서 인증번호를 확인해 주세요.' }}
              <span v-if="emailVerification.timer > 0" class="sonb__timer">
                {{ emailVerification.formatTimer(emailVerification.timer) }}
              </span>
            </p>
          </div>

          <div class="sui-field">
            <label for="ob-phone">대표 연락처</label>
            <div class="sonb__inline">
              <input
                id="ob-phone"
                v-model="form.phone"
                type="tel"
                class="sui-input"
                inputmode="numeric"
                autocomplete="tel"
                maxlength="11"
                placeholder="숫자만 입력"
                required
                :disabled="phoneLocked"
                @input="form.phone = sanitizeDigitsInput(($event.target as HTMLInputElement).value, 11)"
              />
              <button
                type="button"
                class="sui-btn"
                :disabled="!isPhoneValid(form.phone) || phoneVerification.verified || phoneVerification.sending || phoneVerification.resendIn > 0"
                @click="sendPhoneCode"
              >
                {{ phoneVerification.sendActionLabel('인증발송', '재발송') }}
              </button>
            </div>
            <p v-if="phoneVerification.verified" class="sui-hint sonb__verify-ok">휴대폰 인증이 완료되었습니다.</p>
            <p v-else class="sui-hint">구매자 문의·주문 알림에 사용됩니다. 인증이 필요합니다.</p>
          </div>

          <div v-if="!phoneVerification.verified && phoneVerification.codeSent" class="sui-field">
            <label for="ob-phone-code">휴대폰 인증번호</label>
            <div class="sonb__inline">
              <input
                id="ob-phone-code"
                v-model="form.phoneCode"
                type="text"
                class="sui-input"
                inputmode="numeric"
                maxlength="6"
                placeholder="인증번호 6자리"
                @input="form.phoneCode = sanitizeDigitsInput(($event.target as HTMLInputElement).value, 6)"
              />
              <button
                type="button"
                class="sui-btn"
                :disabled="!isVerifyCodeValid(form.phoneCode) || phoneVerification.verifying"
                @click="confirmPhoneCode"
              >
                {{ phoneVerification.verifying ? '확인 중' : '확인' }}
              </button>
            </div>
            <p class="sui-hint" :class="{ 'sonb__verify-err': phoneVerification.errorMessage }">
              {{ phoneVerification.errorMessage || '문자로 받은 인증번호를 입력해 주세요.' }}
              <span v-if="phoneVerification.timer > 0" class="sonb__timer">
                {{ phoneVerification.formatTimer(phoneVerification.timer) }}
              </span>
            </p>
          </div>

          <div class="sui-row sonb__row">
            <div class="sui-field">
              <label for="ob-zip">우편번호</label>
              <div class="sonb__inline">
                <input
                  id="ob-zip"
                  :value="form.zipcode"
                  class="sui-input"
                  placeholder="주소 검색"
                  readonly
                  @click="onSearchAddress"
                />
                <button type="button" class="sui-btn" :disabled="postcodeOpening" @click="onSearchAddress">
                  주소 검색
                </button>
              </div>
            </div>
            <div class="sui-field">
              <label for="ob-addr">출고지 주소</label>
              <input
                id="ob-addr"
                :value="form.address"
                class="sui-input"
                placeholder="주소 검색을 눌러 주세요"
                readonly
                @click="onSearchAddress"
              />
            </div>
          </div>
          <div class="sui-field">
            <label for="ob-addr2">상세 주소</label>
            <input id="ob-addr2" v-model="form.addressDetail" class="sui-input" placeholder="동·호수 등" />
          </div>
        </div>
      </section>

      <section id="sonb-bank" class="sui-card sonb__section" data-tour="seller-onboarding-bank">
        <div class="sui-card__head sonb__section-head">
          <span class="sonb__section-num" aria-hidden="true">{{ stepIndex('bank') }}</span>
          <h2 class="sui-card__title">{{ stepLabel('bank') }}</h2>
        </div>
        <div class="sui-card__body sonb__body">
          <p class="sonb__section-lead">정산금이 입금될 계좌입니다. 예금주는 사업자등록증 상호와 같아야 합니다.</p>
          <div class="sui-row sonb__row">
            <div class="sui-field">
              <label for="ob-bank">은행</label>
              <select id="ob-bank" v-model="form.settleBank" class="sui-select" required>
                <option value="">선택해 주세요</option>
                <option v-for="bank in BANKS" :key="bank" :value="bank">{{ bank }}</option>
              </select>
            </div>
            <div class="sui-field">
              <label for="ob-acc">계좌번호</label>
              <input id="ob-acc" v-model="form.settleAccount" class="sui-input" inputmode="numeric" required />
            </div>
          </div>
          <div class="sui-field">
            <label for="ob-holder">예금주</label>
            <input id="ob-holder" v-model="form.settleHolder" class="sui-input" required />
          </div>
        </div>
      </section>

      <section id="sonb-terms" class="sui-card sonb__section" data-tour="seller-onboarding-terms">
        <div class="sui-card__head sonb__section-head">
          <span class="sonb__section-num" aria-hidden="true">{{ stepIndex('terms') }}</span>
          <h2 class="sui-card__title">{{ stepLabel('terms') }}</h2>
        </div>
        <div class="sui-card__body sonb__body">
          <p class="sonb__section-lead">필수 이용약관을 펼쳐 확인한 뒤 각각 동의해 주세요. 체크는 미리 선택되지 않습니다.</p>
          <label v-if="termDocs.length" class="sonb__check sonb__check--all">
            <input v-model="acceptAllTerms" type="checkbox" @change="onAcceptAllTerms" />
            필수 약관 전체 동의
          </label>
          <details
            v-for="doc in termDocs"
            :key="doc.code"
            class="sonb__doc"
          >
            <summary>
              <span>
                <strong>{{ doc.title }}</strong>
                <em>v{{ doc.version }}</em>
              </span>
              <span class="sonb__doc-state" :class="{ 'is-on': accepted[doc.code] }">
                {{ accepted[doc.code] ? '동의함' : '확인 필요' }}
              </span>
            </summary>
            <pre class="sonb__doc-body">{{ doc.body }}</pre>
            <label class="sonb__check" @click.stop>
              <input v-model="accepted[doc.code]" type="checkbox" @click.stop />
              {{ doc.title }}에 동의합니다
            </label>
          </details>
          <p v-if="!termDocs.length" class="sonb__empty">등록된 이용약관이 없습니다. 관리자에게 문의해 주세요.</p>
        </div>
      </section>

      <section id="sonb-contract" class="sui-card sonb__section" data-tour="seller-onboarding-contract">
        <div class="sui-card__head sonb__section-head">
          <span class="sonb__section-num" aria-hidden="true">{{ stepIndex('contract') }}</span>
          <h2 class="sui-card__title">{{ stepLabel('contract') }}</h2>
        </div>
        <div class="sui-card__body sonb__body">
          <p class="sonb__section-lead">
            약관 동의 후 공급·판매운영 기본계약서를 조·별첨별로 확인하고, 각 섹션과 전자계약 방식에
            체크한 뒤 PDF로 저장할 수 있습니다.
          </p>
          <ContractDocumentView
            v-for="doc in contractDocs"
            :key="doc.code"
            mode="agree"
            :title="doc.title"
            :version="doc.version"
            :effective="docEffective(doc)"
            :body="doc.body"
            :party="{
              storeName: form.storeName || form.bizName,
              ownerName: form.ownerName,
              bizNumber: form.bizNumber,
              address: form.address,
              addressDetail: form.addressDetail,
            }"
            @update:ready="(ready) => onContractReady(doc.code, ready)"
            @update:checks="(items) => onContractChecks(doc.code, items)"
          />
          <p v-if="!contractDocs.length" class="sonb__empty">등록된 계약서가 없습니다. 관리자에게 문의해 주세요.</p>
        </div>
      </section>

      <section id="sonb-signature" class="sui-card sonb__section" data-tour="seller-onboarding-signature">
        <div class="sui-card__head sonb__section-head">
          <span class="sonb__section-num" aria-hidden="true">{{ stepIndex('signature') }}</span>
          <h2 class="sui-card__title">{{ stepLabel('signature') }}</h2>
        </div>
        <div class="sui-card__body sonb__body">
          <p class="sonb__section-lead">
            위 약관·계약서에 대한 동의 의사로 대표자 또는 계약권한자가 서명해 주세요. 서명·문서
            버전·시각이 저장됩니다.
          </p>
          <SignaturePad size="lg" @change="onSignature" />
        </div>
      </section>

      <div id="sonb-submit" class="sonb__actions" data-tour="seller-onboarding-submit">
        <div class="sonb__actions-copy">
          <strong>제출하면 검수 대기(PENDING) 상태가 됩니다.</strong>
          <span>관리자 승인 전에는 상품 등록·판매를 할 수 없습니다.</span>
        </div>
        <button type="submit" class="sui-btn sui-btn--primary sonb__submit" :disabled="submitting">
          {{ submitting ? '제출 중…' : '검수 요청하기' }}
        </button>
      </div>
    </form>

    <aside class="sonb__tracker" data-tour="seller-onboarding-tracker" aria-label="입력 확인">
      <div class="sonb__tracker-card">
        <header class="sonb__tracker-head">
          <strong>입력 확인</strong>
          <span>{{ checklistDoneCount }}/{{ checklist.length }}</span>
        </header>
        <p class="sonb__tracker-lead">항목을 누르면 해당 입력으로 이동합니다.</p>
        <ul class="sonb__tracker-list">
          <li
            v-for="item in checklist"
            :key="item.key"
            class="sonb__tracker-item"
            :class="{ 'is-done': item.done }"
          >
            <button type="button" @click="scrollToChecklistItem(item)">
              <span class="sonb__tracker-mark" aria-hidden="true">
                <AppIcon v-if="item.done" icon="mdi-check" :size="14" />
              </span>
              <span class="sonb__tracker-label">{{ item.label }}</span>
            </button>
          </li>
        </ul>
      </div>
    </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import SignaturePad from '@/components/common/SignaturePad.vue'
import ContractDocumentView, {
  type ContractCheckProgress,
} from '@/components/seller/ContractDocumentView.vue'
import { fetchSellerOnboarding, submitSellerOnboarding, type SellerLegalDoc } from '@/api/seller'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { useDaumPostcode } from '@/composables/useDaumPostcode'
import { useContentSnippets, SNIPPET_FALLBACKS } from '@/composables/useContentSnippets'
import { useVerification } from '@/composables/useVerification'
import { MALL_BANKS } from '@/constants/banks'
import {
  bizNumberInputError,
  formatBizNumberInput,
  isBizNumberInputValid,
  isEmailValid,
  isPhoneValid,
  isVerifyCodeValid,
  sanitizeDigitsInput,
} from '@/utils/formValidation'
import { blobToBase64, buildContractPdfBlob } from '@/utils/contractPdf'

type ChecklistItem = {
  key: string
  label: string
  done: boolean
  target: string
}

const router = useRouter()
const { setSeller, seller } = useSellerAuth()
const { opening: postcodeOpening, searchPostcode } = useDaumPostcode()
const { loadSnippets, snippet } = useContentSnippets()
const emailVerification = useVerification('seller-onboarding')
const phoneVerification = useVerification('seller-onboarding')

const BANKS = MALL_BANKS
const docs = ref<SellerLegalDoc[]>([])
const intro = ref(SNIPPET_FALLBACKS['seller.onboard.intro'])
const pwTemp = ref(false)
const errorMessage = ref('')
const submitting = ref(false)
const signature = ref('')
const acceptAllTerms = ref(false)
const accepted = reactive<Record<string, boolean>>({})
/** 계약서 섹션·전자계약·최종 동의별 진행 (리모컨용) */
const contractCheckMap = reactive<Record<string, ContractCheckProgress[]>>({})

const termDocs = computed(() => docs.value.filter((doc) => doc.kind !== 'CONTRACT'))
const contractDocs = computed(() => docs.value.filter((doc) => doc.kind === 'CONTRACT'))

const form = reactive({
  storeName: '',
  bizName: '',
  bizNumber: '',
  ownerName: '',
  managerName: '',
  email: '',
  emailCode: '',
  phone: '',
  phoneCode: '',
  zipcode: '',
  address: '',
  addressDetail: '',
  settleBank: '',
  settleAccount: '',
  settleHolder: '',
  newPassword: '',
  newPasswordConfirm: '',
})

const emailLocked = computed(
  () => emailVerification.sending || emailVerification.verified,
)
const phoneLocked = computed(
  () => phoneVerification.sending || phoneVerification.verified,
)

const bizNumberError = computed(() => bizNumberInputError(form.bizNumber))

function onBizNumberInput(event: Event) {
  const raw = (event.target as HTMLInputElement).value
  /* 숫자만 붙여넣어도 하이픈을 바로 넣어 000-00-00000 으로 맞춘다 */
  form.bizNumber = formatBizNumberInput(raw)
}

const termsAccepted = computed(
  () => termDocs.value.length > 0 && termDocs.value.every((doc) => accepted[doc.code]),
)
const contractAccepted = computed(
  () =>
    contractDocs.value.length > 0 && contractDocs.value.every((doc) => accepted[doc.code]),
)

const checklist = computed<ChecklistItem[]>(() => {
  const items: ChecklistItem[] = []

  if (pwTemp.value) {
    items.push({
      key: 'password',
      label: '새 비밀번호',
      done: form.newPassword.length >= 8,
      target: 'ob-pw',
    })
    items.push({
      key: 'passwordConfirm',
      label: '비밀번호 확인',
      done:
        form.newPasswordConfirm.length >= 8 &&
        form.newPasswordConfirm === form.newPassword,
      target: 'ob-pw2',
    })
  }

  items.push(
    {
      key: 'storeName',
      label: '업체명',
      done: Boolean(form.storeName.trim()),
      target: 'ob-store',
    },
    {
      key: 'ownerName',
      label: '대표자명',
      done: Boolean(form.ownerName.trim()),
      target: 'ob-owner',
    },
    {
      key: 'bizName',
      label: '상호',
      done: Boolean(form.bizName.trim()),
      target: 'ob-biz',
    },
    {
      key: 'bizNumber',
      label: '사업자등록번호',
      done: isBizNumberInputValid(form.bizNumber),
      target: 'ob-bizno',
    },
    {
      key: 'managerName',
      label: '담당자명',
      done: Boolean(form.managerName.trim()),
      target: 'ob-manager',
    },
    {
      key: 'email',
      label: '이메일 인증',
      done: emailVerification.verified,
      target: 'ob-email',
    },
    {
      key: 'phone',
      label: '휴대폰 인증',
      done: phoneVerification.verified,
      target: 'ob-phone',
    },
    {
      key: 'address',
      label: '출고지 주소',
      done: Boolean(form.zipcode && form.address),
      target: 'ob-zip',
    },
    {
      key: 'bank',
      label: '정산 은행',
      done: Boolean(form.settleBank),
      target: 'ob-bank',
    },
    {
      key: 'account',
      label: '계좌번호',
      done: Boolean(form.settleAccount.replace(/\D/g, '')),
      target: 'ob-acc',
    },
    {
      key: 'holder',
      label: '예금주',
      done: Boolean(form.settleHolder.trim()),
      target: 'ob-holder',
    },
    {
      key: 'terms',
      label: '약관 동의',
      done: termsAccepted.value,
      target: 'sonb-terms',
    },
  )

  for (const doc of contractDocs.value) {
    const checks = contractCheckMap[doc.code] || []
    for (const check of checks) {
      items.push({
        key: `contract-${doc.code}-${check.key}`,
        label: check.label,
        done: check.done,
        target: check.target,
      })
    }
  }

  items.push({
    key: 'signature',
    label: '서명',
    done: Boolean(signature.value),
    target: 'sonb-signature',
  })

  return items
})

const checklistDoneCount = computed(() => checklist.value.filter((item) => item.done).length)

function scrollToChecklistItem(item: ChecklistItem) {
  const el = document.getElementById(item.target)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  if (el instanceof HTMLElement) {
    el.classList.add('sonb__flash')
    window.setTimeout(() => el.classList.remove('sonb__flash'), 1200)
    if (el.matches('input, select, textarea, button')) {
      el.focus({ preventScroll: true })
    }
  }
}

const steps = computed(() => {
  const list = [
    ...(pwTemp.value ? [{ key: 'password', label: '비밀번호' }] : []),
    { key: 'profile', label: '사업자 정보' },
    { key: 'bank', label: '정산 계좌' },
    { key: 'terms', label: '약관 동의' },
    { key: 'contract', label: '계약서' },
    { key: 'signature', label: '서명' },
  ]
  return list
})

const STEP_LABELS: Record<string, string> = {
  password: '임시 비밀번호 변경',
  profile: '사업자 · 담당자 · 출고지',
  bank: '정산 계좌',
  terms: '약관 동의',
  contract: '계약서 확인',
  signature: '서명',
}

function stepIndex(key: string) {
  return steps.value.findIndex((step) => step.key === key) + 1
}

function stepLabel(key: string) {
  return STEP_LABELS[key] || key
}

function docEffective(doc: SellerLegalDoc) {
  return doc.effective || doc.version
}

function onSignature(dataUrl: string) {
  signature.value = dataUrl
}

function onAcceptAllTerms() {
  for (const doc of termDocs.value) {
    accepted[doc.code] = acceptAllTerms.value
  }
}

function onContractReady(code: string, ready: boolean) {
  accepted[code] = ready
}

function onContractChecks(code: string, items: ContractCheckProgress[]) {
  contractCheckMap[code] = items
}

watch(
  () => termDocs.value.map((doc) => accepted[doc.code]),
  (flags) => {
    acceptAllTerms.value = flags.length > 0 && flags.every(Boolean)
  },
)

async function onSearchAddress() {
  try {
    const result = await searchPostcode()
    if (!result) return
    form.zipcode = result.zipcode
    form.address = result.extra ? `${result.address} ${result.extra}` : result.address
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '주소 검색에 실패했습니다.')
  }
}

async function sendEmailCode() {
  form.emailCode = ''
  try {
    await emailVerification.sendEmail(form.email)
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '이메일 인증번호 발송에 실패했습니다.')
  }
}

async function confirmEmailCode() {
  try {
    await emailVerification.verifyEmail(form.email, form.emailCode)
  } catch {
    /* errorMessage는 폼에 표시 */
  }
}

async function sendPhoneCode() {
  form.phoneCode = ''
  try {
    await phoneVerification.sendPhone(form.phone)
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '문자 인증번호 발송에 실패했습니다.')
  }
}

async function confirmPhoneCode() {
  try {
    await phoneVerification.verifyPhone(form.phone, form.phoneCode)
  } catch {
    /* errorMessage는 폼에 표시 */
  }
}

watch(
  () => form.email,
  () => {
    form.emailCode = ''
    emailVerification.reset()
  },
)

watch(
  () => form.phone,
  () => {
    form.phoneCode = ''
    phoneVerification.reset()
  },
)

async function load() {
  errorMessage.value = ''
  try {
    await loadSnippets(['seller.onboard.intro'])
    const data = await fetchSellerOnboarding()
    docs.value = data.docs
    intro.value = data.intro || snippet('seller.onboard.intro')
    pwTemp.value = Boolean(data.seller.pwTemp)
    form.storeName = data.seller.storeName || ''
    form.bizName = data.seller.bizName || ''
    form.bizNumber = formatBizNumberInput(data.seller.bizNumber || '')
    form.ownerName = data.seller.ownerName || ''
    form.managerName = data.seller.managerName || ''
    form.email = data.seller.email || ''
    form.phone = data.seller.phone || ''
    form.zipcode = data.seller.zipcode || ''
    form.address = data.seller.address || ''
    form.addressDetail = data.seller.addressDetail || ''
    form.settleBank = data.seller.settleBank || ''
    form.settleAccount = data.seller.settleAccount || ''
    form.settleHolder = data.seller.settleHolder || ''
    for (const doc of data.docs) {
      accepted[doc.code] = false
    }
    if (!data.seller.mustOnboard) {
      await router.replace('/seller')
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '온보딩 정보를 불러오지 못했습니다.'
  }
}

async function onSubmit() {
  if (pwTemp.value) {
    if (form.newPassword.length < 8) {
      window.alert('새 비밀번호는 8자 이상이어야 합니다.')
      return
    }
    if (form.newPassword !== form.newPasswordConfirm) {
      window.alert('새 비밀번호 확인이 일치하지 않습니다.')
      return
    }
  }
  if (!isBizNumberInputValid(form.bizNumber)) {
    window.alert(bizNumberError.value || '사업자등록번호 숫자 10자리를 입력해 주세요.')
    scrollToChecklistItem({
      key: 'bizNumber',
      label: '사업자등록번호',
      done: false,
      target: 'ob-bizno',
    })
    return
  }
  if (!emailVerification.verified) {
    window.alert('이메일 인증을 완료해 주세요.')
    return
  }
  if (!phoneVerification.verified) {
    window.alert('휴대폰 인증을 완료해 주세요.')
    return
  }
  for (const doc of termDocs.value) {
    if (!accepted[doc.code]) {
      window.alert(`「${doc.title}」에 동의해 주세요.`)
      return
    }
  }
  if (!contractDocs.value.length) {
    window.alert('확인할 계약서가 없습니다. 관리자에게 문의해 주세요.')
    return
  }
  for (const doc of contractDocs.value) {
    if (!accepted[doc.code]) {
      window.alert('계약서 각 섹션·전자계약·최종 동의에 모두 체크해 주세요.')
      return
    }
  }
  if (!signature.value) {
    window.alert('서명란에 서명해 주세요.')
    return
  }
  if (!form.zipcode || !form.address) {
    window.alert('출고지 주소를 검색해 입력해 주세요.')
    return
  }

  submitting.value = true
  try {
    /* 계약서는 제출 시 PDF를 만들어 서버에 체결 스냅샷으로 보관한다 */
    const accepts: Array<{ code: string; version: string; pdfBase64?: string }> = []
    for (const doc of docs.value) {
      const item: { code: string; version: string; pdfBase64?: string } = {
        code: doc.code,
        version: doc.version,
      }
      if (doc.kind === 'CONTRACT') {
        const blob = await buildContractPdfBlob({
          title: doc.title,
          version: doc.version,
          effective: docEffective(doc),
          body: doc.body,
          party: {
            storeName: form.storeName || form.bizName,
            ownerName: form.ownerName,
            bizNumber: form.bizNumber,
            address: form.address,
            addressDetail: form.addressDetail,
          },
          signature: signature.value,
          signerName: form.ownerName || form.managerName,
          electronicMethod: true,
        })
        item.pdfBase64 = await blobToBase64(blob)
      }
      accepts.push(item)
    }

    const next = await submitSellerOnboarding({
      storeName: form.storeName,
      bizName: form.bizName,
      bizNumber: form.bizNumber,
      ownerName: form.ownerName,
      managerName: form.managerName,
      email: form.email,
      phone: form.phone,
      zipcode: form.zipcode,
      address: form.address,
      addressDetail: form.addressDetail,
      settleBank: form.settleBank,
      settleAccount: form.settleAccount,
      settleHolder: form.settleHolder,
      newPassword: pwTemp.value ? form.newPassword : undefined,
      signature: signature.value,
      accepts,
    })
    setSeller(next)
    window.alert('검수 요청이 접수되었습니다.\n관리자 승인 후 판매관리센터에서 상품을 등록할 수 있습니다.')
    await router.replace('/seller')
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '제출에 실패했습니다.')
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  if (!seller.value) {
    router.replace({ name: 'login', query: { tab: 'seller', redirect: '/seller/onboarding' } })
    return
  }
  load()
})
</script>

<style lang="scss" src="./SellerOnboardingPage.scss"></style>
